import { DateTime } from 'luxon';

import {
  ChartMetricResponse,
  ChartTransformResult,
  MetricResponse,
  Period,
} from '../types';

const INVALID_VALUE = -1;
const SECONDS_IN_DAY = 86400;
const SECONDS_IN_HOUR = 3600;
const SECONDS_IN_MINUTE = 60;

export const formatDateForAPI = (date: string | Date): string => {
  return new Date(date).toISOString().replace(/\.\d{3}Z$/, 'Z');
};

export const isChartMetricResponse = (
  response: MetricResponse | MetricResponse[],
): response is ChartMetricResponse => {
  return 'chartData' in response;
};

export const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - 30);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  return {
    start: formatDateForAPI(start),
    end: formatDateForAPI(end),
  };
};

export const formatTimeValue = (timeInSeconds: number | undefined | null) => {
  if (
    timeInSeconds === undefined ||
    timeInSeconds === null ||
    timeInSeconds === INVALID_VALUE
  ) {
    return null;
  }

  const units = [
    { unit: 'day', value: SECONDS_IN_DAY },
    { unit: 'hour', value: SECONDS_IN_HOUR },
    { unit: 'minute', value: SECONDS_IN_MINUTE },
    { unit: 'second', value: 1 },
  ];

  const absValue = Math.abs(timeInSeconds);

  for (const { unit, value } of units) {
    if (absValue >= value || unit === 'second') {
      const result = Number.parseFloat((timeInSeconds / value).toFixed(2));
      return {
        value: result,
        unit: result === 1 ? unit : `${unit}s`,
      };
    }
  }

  return { value: timeInSeconds, unit: 'seconds' };
};

export const formatRelativeTime = (dateString: string): string => {
  try {
    const relative = DateTime.fromISO(dateString).toRelative({ locale: 'en' });
    return relative ?? '-';
  } catch {
    return '-';
  }
};

export const transformChartData = (
  periods: Period[],
  unit?: string,
): ChartTransformResult => {
  const targetUnit = unit || 'hours';

  const values = periods
    .filter(
      period =>
        period.value !== INVALID_VALUE &&
        period.value !== null &&
        period.value !== undefined,
    )
    .map(period => {
      const rawValue = Number(period.value);
      switch (targetUnit) {
        case 'days':
        case 'day':
          return parseFloat((rawValue / SECONDS_IN_DAY).toFixed(2));
        case 'hours':
        case 'hour':
          return parseFloat((rawValue / SECONDS_IN_HOUR).toFixed(2));
        case 'minutes':
        case 'minute':
          return parseFloat((rawValue / SECONDS_IN_MINUTE).toFixed(2));
        default:
          return parseFloat(rawValue.toFixed(2));
      }
    });

  return { values, displayUnit: targetUnit };
};

export const transformNumericChartData = (
  periods: any[],
  valueMapper: (period: any) => number | null | undefined,
  defaultUnit: string,
): ChartTransformResult => {
  const values: number[] = periods
    .map(valueMapper)
    .filter((v): v is number => v !== null && v !== undefined);

  return { values, displayUnit: defaultUnit };
};

export const formatValueWithUnit = (value: any, unit?: string): string => {
  if (value === null || value === undefined) return '-';

  const numValue = Number(value);

  switch (unit) {
    case 'seconds': {
      const timeFormatted = formatTimeValue(numValue);
      return timeFormatted
        ? `${timeFormatted.value.toFixed(2)} ${timeFormatted.unit}`
        : `${numValue.toFixed(2)} seconds`;
    }
    case 'minutes':
    case 'minute':
      return `${numValue.toFixed(2)} ${numValue === 1 ? 'minute' : 'minutes'}`;
    case 'hours':
    case 'hour':
      return `${numValue.toFixed(2)} ${numValue === 1 ? 'hour' : 'hours'}`;
    case 'days':
    case 'day':
      return `${numValue.toFixed(2)} ${numValue === 1 ? 'day' : 'days'}`;
    case 'euro':
      return `€${numValue.toFixed(2)}`;
    case '$':
      return `$${numValue.toFixed(2)}`;
    case 'percentage':
    case '%':
      return `${numValue.toFixed(2)}%`;
    case 'per day':
      return `${numValue.toFixed(2)}/day`;
    case 'count':
      return String(numValue);
    case 'lines':
      return `${numValue.toFixed(2)} lines`;
    default:
      return `${numValue.toFixed(2)}${unit ? ` ${unit}` : ''}`;
  }
};
